// src/auth/screens/ForgotPasswordScreen.jsx — route /forgot-password
//
// The ONE recovery button. POST /auth/password-reset always answers 202 and the
// BACKEND decides which mail goes out (password-reset link for an active user,
// set-password link for one who never onboarded, nothing for a deactivated user
// or a superadmin).
//
// ⛔ ENUMERATION SAFETY: the success panel must stay conditional ("if that
// address has an account…"). Never render the 202 as "email sent", never as
// "no such account" — a divergence turns this screen into an account-existence
// oracle, which is the whole reason the endpoint is shaped this way.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthCard, { AuthNotice, authLabelStyle } from './AuthCard.jsx';
import { requestPasswordReset } from '../passwordResetApi.js';
import { authFallbackCopy } from '../authErrorCopy.js';
import { COLORS } from '../../constants/theme.js';

export default function ForgotPasswordScreen() {
  const { t } = useTranslation(['auth', 'errors']);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(email);
      // 202 — and that is ALL we know. See the enumeration note above.
      setSent(true);
    } catch (err) {
      if (err.code === 'NETWORK') {
        setError(t('errors:auth.network'));
      } else if (err.status === 429) {
        // ⛔ Never word this as "account locked": the rate-limit seat is claimed
        // BEFORE the user lookup, so a 429 says nothing about whether the
        // address exists.
        const secs = err.retry_after_seconds;
        setError(typeof secs === 'number'
          ? t('auth:forgot.rateLimited', { seconds: secs })
          : t('auth:forgot.rateLimitedGeneric'));
      } else if (err.status === 422) {
        setError(t('auth:forgot.invalidEmail'));
      } else {
        // ⛔ NEVER `err.message` here, and least of all on THIS screen. Its
        // whole premise is tightly-controlled neutral copy; buildApiError
        // always sets a non-empty message, so the fallback was unreachable and
        // a 500/503 would print server-authored English on the one surface
        // whose wording is an enumeration guarantee. authErrorCopy.js prints the
        // code (or the 5xx reference) inside our own sentence.
        setError(authFallbackCopy(t, err, 'errors:auth.resetRequestFailed'));
      }
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <AuthCard title={t('auth:forgot.sentTitle')}>
        <AuthNotice tone="success">{t('auth:forgot.sentBody')}</AuthNotice>
        <p style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: 1.5, margin: '0 0 24px' }}>
          {t('auth:forgot.sentHint')}
        </p>
        <Link to="/login" className="auth-link">{t('auth:forgot.backToLogin')}</Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('auth:forgot.title')} intro={t('auth:forgot.intro')}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '24px' }}>
          <label htmlFor="forgot-email" style={authLabelStyle}>{t('auth:forgot.emailLabel')}</label>
          <input
            id="forgot-email"
            name="email"
            type="email"
            // ⭐ The one screen a locked-out user reaches, where a typo yields
            // the SAME neutral 202 as success — so there is no feedback to
            // correct it. Let the password manager fill the address.
            autoComplete="email"
            className="auth-input"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>

        {error && <AuthNotice tone="error">{error}</AuthNotice>}

        <button type="submit" className="auth-submit" disabled={loading || !email}>
          {loading ? t('auth:forgot.submitLoading') : t('auth:forgot.submit')}
        </button>
      </form>

      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <Link to="/login" className="auth-link">{t('auth:forgot.backToLogin')}</Link>
      </div>
    </AuthCard>
  );
}
